import React, { useMemo, useState } from 'react';
import { CachedImage } from '@usepepper/ui/atoms/cached-image';
import { createStyles } from '@/contexts/theme-context';
import { FlatList } from 'react-native-gesture-handler';
import { getCarouselItemSubtitle } from '@/contexts/item-recommendations-utils';
import { ItemListOption } from '@/contexts/item-context-types';
import { ListOption } from '@/components/list-item';
import { Text, TouchableOpacity, View } from 'react-native';
import { TYPOGRAPHY } from '@/styles';
import { useCarouselItemShadowStyles } from '@/hooks/use-carousel-item-shadow-styles';
import { useLayoutContext } from '@/contexts/layout-context';

const ITEM_IMAGE_SIZE = 48;
const ITEM_MARGIN_HORIZONTAL = 4;

export type StackedItemListOption = ListOption<{
  items: ItemListOption[];
}>;

interface Props {
  items: ItemListOption[];
  onSelect: (item: ItemListOption) => void;
}

export const ItemDetailsRelatedItemList: React.FC<Props> = ({
  items,
  onSelect,
}) => {
  const { appDimensions } = useLayoutContext();
  const [shadowStyles] = useCarouselItemShadowStyles();
  const [styles] = useStyles();

  const [layoutWidth, setLayoutWidth] = useState(appDimensions.width);

  const stackedItems = useMemo(() => {
    return Array.from<ItemListOption[], StackedItemListOption>(
      { length: Math.ceil(items.length / 2) },
      (_, index) => {
        const start = index * 2;
        return {
          metadata: {
            items: items.slice(start, (index + 1) * 2),
          },
          title: '',
          value: '',
        };
      },
    );
  }, [items]);

  const listItemWidth = layoutWidth * 0.85;
  return (
    <FlatList<StackedItemListOption>
      data={stackedItems}
      decelerationRate="fast"
      getItemLayout={(_, index) => ({
        index,
        length: listItemWidth,
        offset: listItemWidth * index,
      })}
      horizontal
      onLayout={(e) => setLayoutWidth(e.nativeEvent.layout.width)}
      pagingEnabled
      renderItem={(option) => {
        const optionItems = option.item.metadata!.items;
        return (
          <View collapsable={false}>
            {optionItems.map((item) => {
              const uri = item.metadata?.photoURLs[0];
              return (
                <TouchableOpacity
                  key={item.metadata?.variantPackUUID}
                  onPress={() => onSelect(item)}
                  style={[
                    styles.listItem,
                    shadowStyles.shadow,
                    { width: listItemWidth },
                  ]}
                >
                  <View
                    style={[
                      styles.imageContainer,
                      uri ? null : styles.imageContainerEmpty,
                    ]}
                  >
                    {uri ? (
                      <CachedImage
                        contentFit="contain"
                        source={{ uri }}
                        style={styles.image}
                      />
                    ) : null}
                  </View>
                  <View style={styles.carouselItemTextContainer}>
                    <Text numberOfLines={1} style={TYPOGRAPHY.pSemiBold}>
                      {item.title}
                    </Text>
                    <Text numberOfLines={1} style={TYPOGRAPHY.listSubtitle}>
                      {getCarouselItemSubtitle(item.metadata?.skus ?? [])}
                    </Text>
                  </View>
                </TouchableOpacity>
              );
            })}
          </View>
        );
      }}
      showsHorizontalScrollIndicator={false}
      snapToInterval={listItemWidth + 2 * ITEM_MARGIN_HORIZONTAL}
      style={styles.list}
    />
  );
};

const useStyles = createStyles((theme) => ({
  carouselItemTextContainer: {
    flex: 1,
  },
  image: {
    height: ITEM_IMAGE_SIZE,
    width: ITEM_IMAGE_SIZE,
  },
  imageContainer: {
    marginRight: 8,
  },
  imageContainerEmpty: {
    height: ITEM_IMAGE_SIZE,
  },
  list: {
    overflow: 'visible',
  },
  listItem: {
    alignItems: 'center',
    backgroundColor: theme.colors.white,
    borderRadius: 4,
    flexDirection: 'row',
    marginHorizontal: ITEM_MARGIN_HORIZONTAL,
    marginVertical: 4,
    padding: 8,
  },
}));
